/*
 * Copyright (c) 2023. Collabro Limited
 */

import React, { memo, useContext, useState } from "react";
import {
  AppBar,
  Avatar,
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { logoutUser } from "../../api";
import { AuthenticatedContext } from "../Authenticated/Authenticated";

interface HeaderComponent {
  setLogout: any;
  logout: boolean;
}

const Header = (props: HeaderComponent) => {
  const { loggedInUser, currentLocation } = useContext(AuthenticatedContext);
  const theme = useTheme();

  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);

  const removeSession = useMutation({
    mutationFn: () => {
      return logoutUser();
    },
  });

  const handleLogout = () => {
    props.setLogout(true);
    setAnchorElUser(null);
    removeSession.mutate();
  };

  const handleOpenMenu = (event: any) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorElUser(null);
  };

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        background: (theme) => theme.palette.modal.main,
      }}
    >
      <Toolbar>
        <img src={theme.logo.small} alt="logo" width={50} />
        <Typography
          variant="h6"
          ml={1}
          color="primary"
          sx={{
            display: { xs: "none", lg: "flex" },
          }}
        >
          Roster Panel
        </Typography>
        <Chip
          size="small"
          sx={{ ml: 1 }}
          label={
            process.env.REACT_APP_VERSION
              ? process.env.REACT_APP_VERSION
              : "dev"
          }
        />
        <Box sx={{ flexGrow: 0.6 }} />
        <Box sx={{ display: "flex" }}>
          <Typography
            alignSelf="center"
            variant="h6"
            color="primary"
            maxWidth="10rem"
            whiteSpace="nowrap"
          >
            {currentLocation &&
              loggedInUser &&
              !props.logout &&
              currentLocation.description}
          </Typography>
        </Box>
        <Box sx={{ flexGrow: 0.6 }} />
        <Typography
          variant="subtitle2"
          mr={1}
          color="primary"
          sx={{
            display: { xs: "none", md: "flex" },
          }}
        >
          Copyright (c) 2023. Collabro Limited
        </Typography>
        {loggedInUser && !props.logout && (
          <>
            <Box sx={{ flexGrow: 0 }}>
              <Tooltip title="Open Menu">
                <IconButton onClick={handleOpenMenu} sx={{ p: 0 }}>
                  <Avatar
                    alt={`${loggedInUser.forenames} ${loggedInUser.surname}`}
                    sx={{
                      bgcolor: (theme) => theme.palette.secondary.main,
                    }}
                  >
                    {`${loggedInUser.forenames[0]}${loggedInUser.surname[0]}`}
                  </Avatar>
                </IconButton>
              </Tooltip>
              <Menu
                open={Boolean(anchorElUser)}
                sx={{ mt: "45px" }}
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                onClose={handleCloseMenu}
              >
                <MenuItem>
                  <Typography>{`${loggedInUser.forenames} ${loggedInUser.surname}`}</Typography>
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <Typography>Sign Out</Typography>
                </MenuItem>
              </Menu>
            </Box>
          </>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default memo(Header);
